<template>
  <PageTitle @back="onBack">Switch Company</PageTitle>
  <div class="content">
    <div class="card mb-3">
      <div v-if="loading" class="spinner-wrapper">
        <SpinnerLogo />
      </div>
      <div v-else-if="loadError">
        <div class="alert alert-danger mb-2">Couldn't load companies — try again.</div>
        <button type="button" class="btn btn-primary btn-block" @click="load">Retry</button>
      </div>
      <div v-else>
        <p class="mb-1 font-w600">Choose a company to switch to</p>
        <CompanySelector
          v-model="selectedCompanyId"
          :companies="companyStore.globalCompanies"
          :current-company-id="currentCompanyId"
        />
      </div>
    </div>

    <div v-if="targetCompany" class="card">
      <p class="font-w600 mb-2">Confirm</p>
      <p class="mb-2">{{ confirmMessage }}</p>
      <div v-if="switchError" class="alert alert-danger mb-2">{{ switchError }}</div>
      <button
        type="button"
        class="btn btn-primary btn-block"
        :disabled="switching"
        @click="onConfirmSwitch"
      >
        {{ switching ? 'Switching...' : `Switch to ${targetCompany.name}` }}
      </button>
      <button
        type="button"
        class="btn btn-secondary btn-block"
        :disabled="switching"
        @click="onCancel"
      >
        Cancel
      </button>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';

import PageTitle from '@/components/PageTitle.vue';
import SpinnerLogo from '@/components/SpinnerLogo.vue';
import CompanySelector from '@/components/CompanySelector.vue';

import {
  useStore as useCompanyStore,
  type ETNCompanyListItem
} from '@/stores/company';
import { useStore as useQueueStore } from '@/stores/queue';
import { flush, track } from '@/lib/usage';
import { withTimeout as withUsageTimeout } from '@/utils/networkUtils';

const $router = useRouter();
const { user, loginWithRedirect } = useAuth0();
const USAGE_FLUSH_TIMEOUT_MS = 2000;
const companyStore = useCompanyStore();
const queueStore = useQueueStore();

// If the API doesn't respond within this window we surface an error rather
// than letting the UI hang. Covers slow/flaky connections that don't trip
// the browser's offline detection.
const REQUEST_TIMEOUT_MS = 15000;

const withTimeout = <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Request timed out')),
      REQUEST_TIMEOUT_MS
    );
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  });
};

const selectedCompanyId = ref<string | null>(null);
const loading = ref(false);
const loadError = ref(false);
const switching = ref(false);
const switchError = ref<string | null>(null);

const currentCompanyId = computed<string | undefined>(
  () => user.value?.['https://app.etainabl.com/companyId'] as string | undefined
);

const targetCompany = computed<ETNCompanyListItem | null>(() => {
  if (!selectedCompanyId.value) return null;
  if (selectedCompanyId.value === currentCompanyId.value) return null;
  return companyStore.globalCompanies.find((c) => c._id === selectedCompanyId.value) ?? null;
});

const pendingQueueCount = computed(() => queueStore.queue.length);

const currentCompanyName = computed<string>(() => {
  const id = currentCompanyId.value;
  if (!id) return '';
  const match = companyStore.globalCompanies.find((c) => c._id === id);
  return match?.name || companyStore.company?.name || '';
});

const confirmMessage = computed(() => {
  if (!targetCompany.value) return '';
  const target = targetCompany.value.name;
  if (pendingQueueCount.value > 0) {
    const noun = pendingQueueCount.value === 1 ? 'reading' : 'readings';
    const current = currentCompanyName.value || 'the current company';
    return (
      `Switch to ${target}? Any reading you're partway through will be lost. ` +
      `You have ${pendingQueueCount.value} pending ${noun} for ${current}. ` +
      `They'll stay safely saved and will reappear when you switch back.`
    );
  }
  return `Switch to ${target}? Any reading you're partway through will be lost.`;
});

const load = async () => {
  loading.value = true;
  loadError.value = false;
  try {
    await withTimeout(companyStore.getGlobalCompanies());
  } catch (e) {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

onBeforeMount(() => {
  if (companyStore.globalCompanies.length === 0) {
    load();
  }
});

const onBack = () => {
  // window.history.state.back is null when the current entry is the first in
  // the session (e.g. /switch-company opened via direct link or bookmark).
  // Calling $router.back() in that case exits the SPA — push to home instead.
  if (window.history.state?.back) {
    $router.back();
  } else {
    $router.push({ name: 'select' });
  }
};

const onCancel = () => {
  if (switching.value) return;
  selectedCompanyId.value = null;
};

const onConfirmSwitch = async () => {
  if (!targetCompany.value) return;
  if (!user.value?.sub) {
    switchError.value = "Couldn't switch — please reload and try again.";
    return;
  }

  switching.value = true;
  switchError.value = null;

  try {
    await withTimeout(
      companyStore.setCompanyId({
        userSub: user.value.sub,
        companyId: targetCompany.value._id
      })
    );

    track('company_switched', {
      from_company_id: String(user.value?.['https://app.etainabl.com/companyId'] || ''),
      to_company_id: String(targetCompany.value._id)
    });
    await withUsageTimeout(flush(), USAGE_FLUSH_TIMEOUT_MS).catch(() => {});

    await loginWithRedirect({
      appState: {
        target: '/select'
      }
    });
    // Browser navigates away; nothing after this runs.
  } catch (e) {
    switching.value = false;
    switchError.value = "Couldn't switch — check your connection and try again.";
  }
};
</script>
